<template>
  <b-container class="notifications-page my-4">
    <div v-if="isWeb2UserConnected">
      <div class="page-header d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div>
          <h1 class="mb-0">Notifications</h1>
          <p class="text-muted mb-0">Manage your alerts and how you want to receive them.</p>
        </div>
      </div>

      <b-tabs v-model="activeTab" content-class="mt-3">
        <b-tab title="Feed">
          <div class="d-flex justify-content-end mb-2">
            <b-button size="sm" variant="outline-secondary" :disabled="!notifications.length" @click="markAllRead">
              Mark all as read
            </b-button>
          </div>

          <div v-if="notifLoading" class="text-center text-muted py-5">Loading…</div>

          <div v-else-if="!notifications.length" class="empty-state text-center py-5">
            <i class="fas fa-bell-slash mb-2"></i>
            <p class="mb-0">You're all caught up — no notifications yet.</p>
          </div>

          <div v-else class="notif-list">
            <div
              v-for="n in notifications"
              :key="n.id"
              class="notif-row"
              :class="{ 'is-unread': !n.readAt }"
              @click="openNotification(n)"
            >
              <span class="notif-icon" :class="'level-' + n.level">
                <i class="fas" :class="levelIcon(n.level)"></i>
              </span>
              <div class="notif-body">
                <div class="notif-title">{{ n.title }}</div>
                <div class="notif-message">{{ n.message }}</div>
                <div class="notif-time">{{ formatTime(n.createdAt) }}</div>
              </div>
              <span v-if="!n.readAt" class="notif-dot"></span>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-3" v-if="hasMore">
            <b-button size="sm" variant="outline-secondary" :disabled="notifLoading" @click="loadMore">
              Load more
            </b-button>
          </div>
        </b-tab>

        <b-tab title="Preferences">
          <div v-if="preferences">
            <template v-if="isAdmin">
              <b-row class="mb-3">
                <b-col md="6">
                  <b-form-group label="Minimum wallet balance (xDai)">
                    <b-form-input v-model.number="preferences.minWalletBalanceXdai" type="number" step="0.01" min="0"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Minimum ZCO2 stock">
                    <b-form-input v-model.number="preferences.minZco2Stock" type="number" step="1" min="0"></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
            </template>

            <template v-for="(group, index) in visibleGroups">
              <h2 class="section-title" :key="group.key" :class="{ 'mt-4': index > 0 }">{{ group.label }}</h2>
              <p class="text-muted" :key="group.key + '-desc'">{{ group.description }}</p>

              <div class="type-card" v-for="type in group.types" :key="type.key">
                <div class="type-info">
                  <i class="fas" :class="type.icon"></i>
                  <div>
                    <div class="type-label">{{ type.label }}</div>
                    <div class="type-description">{{ type.description }}</div>
                  </div>
                </div>
                <div class="type-toggles">
                  <b-form-checkbox v-model="preferences.settings[type.key].enabled" switch>Enabled</b-form-checkbox>
                  <b-form-checkbox v-model="preferences.settings[type.key].platform" switch :disabled="!preferences.settings[type.key].enabled">Feed</b-form-checkbox>
                  <b-form-checkbox v-model="preferences.settings[type.key].email" switch :disabled="!preferences.settings[type.key].enabled">Email</b-form-checkbox>
                  <b-form-checkbox v-model="preferences.settings[type.key].browser" switch :disabled="!preferences.settings[type.key].enabled">Browser</b-form-checkbox>
                </div>
              </div>
            </template>

            <h2 class="section-title mt-4">Email recipients</h2>
            <p class="text-muted">Who receives email notifications. Your account email is included by default — remove it or add others as needed.</p>

            <div class="email-chip-row mb-2">
              <span v-if="preferences.useAccountEmail" class="email-chip email-chip-account">
                <i class="fas fa-user mr-1"></i>{{ getWeb2ActiveAccount.email }}
                <i class="fas fa-times" title="Remove my account email" @click="preferences.useAccountEmail = false"></i>
              </span>
              <span class="email-chip" v-for="email in preferences.extraEmails" :key="email">
                {{ email }}
                <i class="fas fa-times" @click="removeEmail(email)"></i>
              </span>
              <span v-if="!preferences.useAccountEmail && !preferences.extraEmails.length" class="text-muted">
                No recipients added — email notifications won't be sent.
              </span>
            </div>
            <div class="d-flex flex-wrap align-items-center" style="gap: 8px;">
              <b-input-group class="mb-2" style="max-width: 420px;">
                <b-form-input v-model="newEmail" placeholder="name@example.com" @keyup.enter="addEmail"></b-form-input>
                <b-input-group-append>
                  <b-button variant="secondary" @click="addEmail">Add</b-button>
                </b-input-group-append>
              </b-input-group>
              <b-button v-if="!preferences.useAccountEmail" size="sm" variant="outline-secondary" class="mb-2"
                        @click="preferences.useAccountEmail = true">
                <i class="fas fa-plus mr-1"></i>Add my email back
              </b-button>
            </div>

            <h2 class="section-title">Browser notifications</h2>
            <p class="text-muted">
              Status:
              <strong>{{ browserPermissionLabel }}</strong>
            </p>
            <b-button
              v-if="browserPermission !== 'granted'"
              variant="outline-secondary"
              size="sm"
              class="mb-4"
              @click="requestBrowserPermission"
            >
              Enable browser notifications
            </b-button>

            <div v-if="preferences.unsubscribedAll" class="alert-unsubscribed mb-4">
              <i class="fas fa-triangle-exclamation mr-2"></i>
              You previously unsubscribed from all emails via an unsubscribe link. Saving preferences below will
              re-enable email delivery for the types you have turned on.
            </div>

            <div class="d-flex justify-content-end flex-wrap" style="gap: 8px;">
              <b-button variant="outline-secondary" :disabled="sendingTest" @click="sendTestEmail">
                {{ sendingTest ? 'Sending…' : 'Send test email' }}
              </b-button>
              <b-button variant="primary" :disabled="saving" @click="savePreferences">
                {{ saving ? 'Saving…' : 'Save preferences' }}
              </b-button>
            </div>
          </div>
          <div v-else class="text-center text-muted py-5">Loading…</div>
        </b-tab>
      </b-tabs>
    </div>

    <div v-else class="text-center py-5">
      <h1>Sign in required</h1>
      <p>You need to be signed in to view your notifications.</p>
      <router-link class="btn btn-primary mt-2" to="/dashboard">Sign in</router-link>
    </div>
  </b-container>
</template>

<script>
import {mapGetters} from 'vuex'
import moment from 'moment'
import Swal from 'sweetalert2'
import NotificationApi from '../api/Notification'
import NotificationPreference from '../api/NotificationPreference'
import socket from '../utils/socket'
import {NOTIFICATION_TYPES, NOTIFICATION_GROUPS, LEVEL_ICON} from '../constants/notificationTypes'

export default {
  name: 'Notifications',

  data() {
    return {
      activeTab: 0,
      notifications: [],
      notifLoading: false,
      page: 1,
      pageSize: 20,
      total: 0,
      preferences: null,
      newEmail: '',
      saving: false,
      sendingTest: false,
      browserPermission: (window.Notification && window.Notification.permission) || 'unsupported',
      unsubscribeSocket: null
    }
  },

  computed: {
    ...mapGetters('user', ['isWeb2UserConnected', 'getWeb2ActiveAccount']),
    isAdmin() {
      return this.isWeb2UserConnected && this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role.name === 'Administrator'
    },
    adminTypes() {
      return NOTIFICATION_TYPES.filter((t) => t.audience === 'administrator')
    },
    userTypes() {
      return NOTIFICATION_TYPES.filter((t) => t.audience === 'user')
    },
    visibleGroups() {
      const visibleTypes = NOTIFICATION_TYPES.filter((t) => t.audience === 'user' || this.isAdmin)
      return NOTIFICATION_GROUPS
        .map((g) => ({ ...g, types: visibleTypes.filter((t) => t.group === g.key) }))
        .filter((g) => g.types.length)
    },
    hasMore() {
      return this.notifications.length < this.total
    },
    browserPermissionLabel() {
      if (this.browserPermission === 'unsupported') return 'Not supported in this browser'
      if (this.browserPermission === 'granted') return 'Enabled'
      if (this.browserPermission === 'denied') return 'Blocked — enable it in your browser settings'
      return 'Not enabled yet'
    }
  },

  created() {
    // isWeb2UserConnected can still be false here on a fresh page load —
    // the session-restore-from-token action is async and may not have
    // resolved yet, so a one-time check-and-skip would silently never load
    // anything. Watching with `immediate` covers both cases: already
    // connected now, or becomes connected moments later.
    this.$watch(
      'isWeb2UserConnected',
      (connected) => {
        if (!connected) return
        this.loadNotifications()
        this.loadPreferences()

        if (this.unsubscribeSocket) this.unsubscribeSocket()
        this.unsubscribeSocket = socket.onNotification(() => {
          this.loadNotifications()
        })
      },
      { immediate: true }
    )
  },

  beforeDestroy() {
    if (this.unsubscribeSocket) this.unsubscribeSocket()
  },

  methods: {
    levelIcon(level) {
      return LEVEL_ICON[level] || LEVEL_ICON.info
    },
    formatTime(date) {
      return moment(date).fromNow()
    },
    loadNotifications() {
      this.notifLoading = true
      NotificationApi.getNotifications(1, this.pageSize)
        .then((res) => {
          this.notifications = res.data
          this.total = (res.meta && res.meta.total) || res.data.length
          this.page = 1
        })
        .finally(() => {
          this.notifLoading = false
        })
    },
    loadMore() {
      this.notifLoading = true
      const nextPage = this.page + 1
      NotificationApi.getNotifications(nextPage, this.pageSize)
        .then((res) => {
          this.notifications = this.notifications.concat(res.data)
          this.page = nextPage
        })
        .finally(() => {
          this.notifLoading = false
        })
    },
    openNotification(n) {
      if (!n.readAt) {
        NotificationApi.markRead(n.id).then(() => {
          n.readAt = new Date().toISOString()
          this.$root.$emit('zc-notifications:read')
        })
      }
      if (n.link) this.$router.push(n.link)
    },
    markAllRead() {
      NotificationApi.markAllRead().then(() => {
        this.notifications.forEach((n) => {
          if (!n.readAt) n.readAt = new Date().toISOString()
        })
        this.$root.$emit('zc-notifications:read')
      })
    },
    loadPreferences() {
      NotificationPreference.getMyPreferences().then((res) => {
        this.preferences = res.data
      })
    },
    addEmail() {
      const email = (this.newEmail || '').trim().toLowerCase()
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        Swal.fire('Error', 'Please enter a valid email address', 'error')
        return
      }
      if (this.preferences.extraEmails.includes(email)) {
        this.newEmail = ''
        return
      }
      this.preferences.extraEmails.push(email)
      this.newEmail = ''
    },
    removeEmail(email) {
      this.preferences.extraEmails = this.preferences.extraEmails.filter((e) => e !== email)
    },
    // A type can't have its email channel on with nowhere to send it —
    // mirrors the authoritative check in new-server's updateMe controller,
    // just so the user gets immediate feedback instead of a round-trip.
    findEmailWithoutRecipients() {
      const hasRecipients = this.preferences.useAccountEmail || this.preferences.extraEmails.length > 0
      if (hasRecipients) return null
      const allTypes = this.adminTypes.concat(this.userTypes)
      const offending = allTypes.find((t) => {
        const setting = this.preferences.settings[t.key]
        return setting && setting.enabled && setting.email
      })
      return offending ? offending.label : null
    },
    savePreferences() {
      const offendingLabel = this.findEmailWithoutRecipients()
      if (offendingLabel) {
        Swal.fire(
          'Missing recipient',
          `"${offendingLabel}" has email enabled but there's no recipient address. Add an email above or turn off its email toggle.`,
          'warning'
        )
        return
      }

      this.saving = true
      NotificationPreference.updateMyPreferences({
        settings: this.preferences.settings,
        extraEmails: this.preferences.extraEmails,
        useAccountEmail: this.preferences.useAccountEmail,
        minWalletBalanceXdai: this.preferences.minWalletBalanceXdai,
        minZco2Stock: this.preferences.minZco2Stock
      })
        .then((res) => {
          this.preferences = res.data
          this.$toasted.show('Your notification preferences have been saved', {
            type: 'success',
            duration: 5000,
            theme: 'bubble',
            position: 'top-center'
          })
        })
        .catch((error) => {
          Swal.fire('Error', error.response?.data?.error?.message || error.message, 'error')
        })
        .finally(() => {
          this.saving = false
        })
    },
    sendTestEmail() {
      this.sendingTest = true
      NotificationPreference.sendTestEmail()
        .then(() => {
          this.$toasted.show('Test email sent — check your inbox', {
            type: 'success',
            duration: 5000,
            theme: 'bubble',
            position: 'top-center'
          })
        })
        .catch((error) => {
          Swal.fire('Error', error.response?.data?.error?.message || error.message, 'error')
        })
        .finally(() => {
          this.sendingTest = false
        })
    },
    requestBrowserPermission() {
      if (!window.Notification) return
      window.Notification.requestPermission().then((permission) => {
        this.browserPermission = permission
      })
    }
  }
}
</script>

<style scoped lang="scss">
.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-heading);
  margin-bottom: 2px;
}

.notif-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.notif-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  cursor: pointer;
  transition: border-color 0.18s ease, transform 0.15s ease;
  position: relative;

  &:hover {
    border-color: var(--accent-green);
    transform: translateY(-1px);
  }

  &.is-unread {
    border-color: rgba(var(--accent-green-rgb), 0.4);
    background: rgba(var(--accent-green-rgb), 0.05);
  }
}

.notif-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(var(--accent-green-rgb), 0.12);
  color: var(--accent-green);

  &.level-warning {
    background: rgba(232, 179, 57, 0.16);
    color: #e8b339;
  }

  &.level-critical {
    background: rgba(232, 93, 93, 0.16);
    color: #e85d5d;
  }
}

.notif-body {
  flex: 1;
  min-width: 0;
}

.notif-title {
  font-weight: 700;
  color: var(--text-heading);
}

.notif-message {
  color: var(--text-main);
  font-size: 0.9rem;
  margin-top: 2px;
}

.notif-time {
  color: var(--text-muted);
  font-size: 0.75rem;
  margin-top: 4px;
}

.notif-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent-green);
  flex-shrink: 0;
  margin-top: 6px;
}

.empty-state {
  color: var(--text-muted);

  i {
    font-size: 2rem;
  }
}

.type-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  margin-bottom: 10px;
}

.type-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 240px;
  flex: 1;

  i {
    width: 20px;
    text-align: center;
    color: var(--accent-green);
    margin-top: 3px;
  }
}

.type-label {
  font-weight: 700;
  color: var(--text-heading);
}

.type-description {
  color: var(--text-muted);
  font-size: 0.82rem;
}

.type-toggles {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.email-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.email-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  font-size: 0.85rem;

  i {
    cursor: pointer;
    color: var(--text-muted);

    &:hover {
      color: #e85d5d;
    }
  }
}

.email-chip-account {
  border-color: rgba(var(--accent-green-rgb), 0.4);
  background: rgba(var(--accent-green-rgb), 0.08);

  i.fa-user {
    color: var(--accent-green);
    cursor: default;
  }
}

.alert-unsubscribed {
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(232, 179, 57, 0.12);
  border: 1px solid rgba(232, 179, 57, 0.35);
  color: var(--text-main);
  font-size: 0.88rem;
}
</style>
